import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import {
  LayoutDashboard, BookOpen, Video, Users, CreditCard,
  HeadphonesIcon, LogOut, Settings, BarChart3, MessageSquare, X,
  Trophy, Zap, Award, Target, PenSquare, BookMarked, MessageCircleQuestion, Briefcase,
  ChevronDown, ChevronRight, Bell, Sun, Newspaper, Calculator, UserCheck, Globe, Image, Lock, Layers, Shield
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";

// ── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logoutUrl } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === "light";

  // For accordion groups (opens downward — Trading Tools, Performance, Account)
  const [openGroups, setOpenGroups] = useState({
    "Learning": true,
    "Learning & Content": true,
    "Learning Management": true,
    "Trading Tools": true,
    "Performance": true,
    "Account": false,
  });
  const toggleGroup = (group) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const studentGroups = [
    {
      group: "Main",
      items: [
        { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" }
      ]
    },
    {
      group: "Learning",
      icon: BookOpen,
      items: [
        { path: "/live-classes",       icon: Video,                 label: "Live Classes" },
        { path: "/batches",            icon: Layers,                label: "Live Batches" },
        { path: "/quizzes",            icon: Target,                label: "Quizzes" },
        { path: "/doubts",             icon: MessageCircleQuestion, label: "Doubt Centre" },
        { path: "/certificates",       icon: Award,                 label: "Certificates" },
      ]
    },
    {
      group: "Trading Tools",
      icon: Zap,
      items: [
        { path: "/drishti",    icon: Zap,        label: "Drishti AI" },
        { path: "/calculator", icon: Calculator, label: "Risk & Lot Calculator" },
        { path: "/briefs",     icon: Newspaper,  label: "Market Briefs" },
        { path: "/broker",     icon: Briefcase,  label: "My Dhan" },
      ]
    },
    {
      group: "Performance",
      icon: BarChart3,
      items: [
        { path: "/journal",     icon: BookMarked, label: "Trade Journal" },
        { path: "/leaderboard", icon: Trophy,     label: "Leaderboard" },
      ]
    },
    {
      group: "Account",
      icon: Users,
      items: [
        { path: "/referrals", icon: Users,          label: "Referrals" },
        { path: "/payments",  icon: CreditCard,     label: "Payments" },
        { path: "/support",   icon: HeadphonesIcon, label: "Support" },
      ]
    }
  ];

  const adminGroups = [
    {
      group: "Main",
      items: [
        { path: "/admin",          icon: BarChart3,       label: "Analytics" },
        { path: "/management",     icon: Shield,          label: "Management" },
        { path: "/admin/site-seo", icon: Globe,           label: "Site & SEO" },
        { path: "/dashboard",      icon: LayoutDashboard, label: "Student View" },
        { path: "/admin/team",     icon: UserCheck,       label: "Team Management" },
      ]
    },
    {
      group: "Trading Tools",
      icon: Zap,
      items: [
        { path: "/admin/alerts", icon: Zap,        label: "Drishti Alerts" },
        { path: "/drishti",      icon: Target,     label: "Drishti AI" },
        { path: "/calculator",   icon: Calculator, label: "Risk & Lot Calculator" },
        { path: "/briefs",       icon: Newspaper,  label: "Market Briefs" },
        { path: "/broker",       icon: Briefcase,  label: "My Dhan" },
      ]
    },
    {
      group: "Learning Management",
      icon: BookOpen,
      items: [
        { path: "/live-classes",   icon: Video,                 label: "Live Classes" },
        { path: "/doubts",         icon: MessageCircleQuestion, label: "Doubt Centre" },
      ]
    },
    {
      group: "Performance & CRM",
      icon: Users,
      items: [
        { path: "/admin/team",  icon: UserCheck,  label: "Team Management" },
        { path: "/admin?tab=referrals", icon: Users, label: "Referrals & Affiliates" },
        { path: "/journal",     icon: BookMarked, label: "Trade Journal" },
        { path: "/leaderboard", icon: Trophy,     label: "Leaderboard" },
        { path: "/admin/leads", icon: Users,      label: "Contact Us Leads" },
        { path: "/counselor",   icon: MessageSquare, label: "Support" },
      ]
    }
  ];

  const techGroups = [
    {
      group: "Main",
      items: [
        { path: "/admin/site-seo", icon: Globe,           label: "Site & SEO" },
        { path: "/dashboard",      icon: LayoutDashboard, label: "Student View" },
      ]
    },
    {
      group: "Trading Tools",
      icon: Zap,
      items: [
        { path: "/drishti",    icon: Zap,        label: "Drishti AI" },
        { path: "/calculator", icon: Calculator, label: "Risk & Lot Calculator" },
        { path: "/briefs",     icon: Newspaper,  label: "Market Briefs" },
      ]
    },
    {
      group: "Support",
      icon: Users,
      items: [
        { path: "/admin?tab=referrals", icon: Users, label: "Referrals" },
        { path: "/admin/leads", icon: Users,        label: "Contact Us Leads" },
        { path: "/counselor",   icon: MessageSquare, label: "Support" },
      ]
    }
  ];

  const mentorGroups = [
    {
      group: "Main",
      items: [
        { path: "/mentor-dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { path: "/dashboard",        icon: LayoutDashboard, label: "Student View" },
      ]
    },
    {
      group: "Learning & Content",
      icon: BookOpen,
      items: [
        { path: "/mentor",      icon: BookOpen,              label: "Content" },
        { path: "/live-classes",icon: Video,                 label: "Live Classes" },
        { path: "/doubts",      icon: MessageCircleQuestion, label: "Doubt Centre" },
      ]
    },
    {
      group: "Trading",
      icon: Zap,
      items: [
        { path: "/drishti",    icon: Zap,        label: "Drishti AI" },
        { path: "/calculator", icon: Calculator, label: "Risk & Lot Calculator" },
        { path: "/briefs",     icon: Newspaper,  label: "Market Briefs" },
        { path: "/broker",     icon: Briefcase,  label: "My Dhan" },
      ]
    },
    {
      group: "Support",
      icon: Users,
      items: [
        { path: "/referrals",   icon: Users,        label: "Referrals & Rewards" },
        { path: "/admin/leads", icon: Users,        label: "Contact Us Leads" },
        { path: "/counselor",   icon: MessageSquare, label: "Support" },
      ]
    }
  ];

  const managementGroups = [
    {
      group: "Main",
      items: [
        { path: "/management",     icon: Shield,          label: "Management" },
        { path: "/dashboard",      icon: LayoutDashboard, label: "Student View" },
      ]
    },
    {
      group: "Live & Content",
      icon: BookOpen,
      items: [
        { path: "/admin/batches",  icon: Layers,          label: "Live Batches" },
        { path: "/live-classes",   icon: Video,           label: "Live Classes" },
      ]
    },
    {
      group: "Trading Tools",
      icon: Zap,
      items: [
        { path: "/drishti",        icon: Zap,             label: "Drishti AI" },
        { path: "/calculator",     icon: Calculator,      label: "Risk & Lot Calculator" },
        { path: "/briefs",         icon: Newspaper,       label: "Market Briefs" },
      ]
    }
  ];

  const getSidebarGroups = () => {
    if (user?.role === "admin") return adminGroups;
    if (user?.role === "management") return managementGroups;
    if (user?.role === "mentor") return mentorGroups;
    if (user?.role === "counselor") return [{ group: "Main", items: [{ path: "/counselor", icon: MessageSquare, label: "Support" }, { path: "/dashboard", icon: LayoutDashboard, label: "Student View" }] }];
    if (user?.role === "tech") return techGroups;
    return studentGroups;
  };

  const sidebarGroups = getSidebarGroups();

  const hasDrishtiAccess = Boolean(
    user?.role === "admin" ||
    user?.role === "superadmin" ||
    user?.has_drishti_access === true
  );

  const isActive = (path) => {
    if (path.includes("?tab=")) {
      const [p, q] = path.split("?");
      return location.pathname === p && location.search.includes(q);
    }
    if (path === "/admin") return location.pathname === "/admin" && !location.search.includes("tab=");
    if (path === "/management") return location.pathname.startsWith("/management");
    if (path === "/admin/site-seo" || path === "/tech") {
      return location.pathname === "/admin/site-seo" || location.pathname === "/tech";
    }
    if (path === "/admin/team") return location.pathname.startsWith("/admin/team");
    if (path === "/admin/alerts") return location.pathname.startsWith("/admin/alerts");
    if (path === "/admin/batches") return location.pathname.startsWith("/admin/batches");
    if (path === "/batches") return location.pathname === "/batches";
    if (path === "/dashboard") return location.pathname === "/dashboard";
    if (path === "/mentor-dashboard") return location.pathname === "/mentor-dashboard";
    if (path === "/mentor") return location.pathname === "/mentor";
    return location.pathname.startsWith(path);
  };


  // Is any item in this group currently active?
  const isGroupActive = (groupObj) => groupObj.items?.some(item => isActive(item.path));

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={onClose} data-testid="sidebar-overlay" />}

      <aside
        data-lenis-prevent="true"
        className={`fixed top-0 left-0 bottom-0 w-[min(18rem,86vw)] md:w-64 border-r z-50 transform transition-all duration-300 md:translate-x-0 flex flex-col ${isOpen ? "translate-x-0" : "-translate-x-full"} ${isLight ? "bg-[#F8F9FA] border-[#E5E7EB]" : "bg-[#0A0A0C] border-white/[0.04] shadow-2xl"}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`h-16 px-5 flex items-center justify-between shrink-0`}>
          <Link to="/dashboard" className="flex items-center gap-2.5" data-testid="sidebar-logo">
            <div className={`w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center ${isLight ? "shadow-xs" : "shadow-xs border border-white/10"}`}>
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className={`text-lg font-black font-outfit tracking-tight ${isLight ? "text-gray-900" : "text-white"}`}>JAMADHAN</span>
          </Link>
          <Button variant="ghost" size="icon" className={`md:hidden ${isLight ? "text-gray-400 hover:text-gray-900" : "text-[#52525B] hover:text-white"}`} onClick={onClose} data-testid="sidebar-close-btn">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <div
          data-lenis-prevent="true"
          className="flex-1 overflow-y-auto overscroll-contain touch-pan-y overflow-x-hidden custom-scrollbar px-3 py-2 flex flex-col gap-2 overscroll-contain touch-pan-y"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
          onWheel={(e) => { if (e.currentTarget) e.currentTarget.scrollTop += e.deltaY; }}
        >
          {sidebarGroups.map((groupObj, groupIdx) => {
            const isGroup = groupObj.group !== "Main";
            const isExpanded = isGroup ? openGroups[groupObj.group] : true;
            const GroupIcon = groupObj.icon;
            const groupHasActiveChild = isGroupActive(groupObj);

            return (
              <div key={groupIdx} className="mb-1">
                {/* Group Header */}
                {isGroup && (
                    // ── Accordion trigger ─────────────────────────────
                    <button
                      onClick={() => toggleGroup(groupObj.group)}
                      className={`w-full flex items-center justify-between px-3 py-2 mb-1 rounded-lg transition-colors ${
                        isLight
                          ? "hover:bg-gray-200/60 text-gray-700"
                          : "hover:bg-white/[0.03] text-gray-400"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {GroupIcon && <GroupIcon className="w-4 h-4 opacity-80" />}
                        <span className="text-xs font-bold tracking-wide uppercase font-outfit">{groupObj.group}</span>
                      </div>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`} />
                    </button>
                )}

                {/* Accordion Items */}
                <ul className={`space-y-0.5 overflow-hidden transition-all ${!isExpanded ? "max-h-0" : "max-h-[1000px] mt-0.5"}`}>
                  {groupObj.items.map((link) => {
                    const active = isActive(link.path);
                    const Icon = link.icon;
                    return (
                      <li key={link.path}>
                        <Link
                          to={link.path}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all group ${
                            active
                              ? isLight
                                ? "bg-white text-emerald-700 shadow-xs font-semibold border border-slate-200/80"
                                : "bg-zinc-800/80 text-white font-semibold border border-white/[0.08]"
                              : isLight
                                ? "text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-transparent font-medium"
                                : "text-zinc-400 hover:text-white hover:bg-white/[0.03] border border-transparent"
                          }`}
                          onClick={() => onClose()}
                          data-testid={`sidebar-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          <Icon className={`w-4 h-4 transition-colors ${
                            active
                              ? isLight ? "text-emerald-600" : "text-emerald-400"
                              : isLight ? "text-slate-400 group-hover:text-slate-800" : "text-zinc-500 group-hover:text-zinc-300"
                          }`} />
                          <span className="truncate">{link.label}</span>
                          {link.path === "/drishti" && hasDrishtiAccess && (
                            <span className="ml-auto text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800">
                              PRO
                            </span>
                          )}
                          {!hasDrishtiAccess && ["/drishti", "/calculator", "/briefs", "/broker", "/journal"].includes(link.path) && (
                            <span className="ml-auto flex items-center gap-1 text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                              <Lock className="w-2.5 h-2.5" /> LOCKED
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>

        {/* User Profile Footer */}
        <div className={`p-4 flex flex-col gap-2 shrink-0 ${isLight ? "bg-white border-t border-gray-200" : "bg-[#0A0A0C] border-t border-white/[0.04]"}`}>
          <div className="flex items-center gap-3 w-full">
            <Avatar className="w-10 h-10 rounded-xl border border-white/10 shrink-0 shadow-sm">
              <AvatarImage src={user?.picture} className="object-cover rounded-xl" />
              <AvatarFallback className={`rounded-xl ${isLight ? "bg-indigo-50 text-indigo-600 font-bold" : "bg-white/5 text-white font-bold"}`}>{user?.name?.charAt(0) || "U"}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold font-outfit truncate ${isLight ? "text-gray-900" : "text-white"}`}>{user?.name || "User"}</p>
              <p className={`text-[10px] font-plex uppercase tracking-wider truncate ${isLight ? "text-gray-500" : "text-[#94A3B8]"}`}>{user?.role || "STUDENT"}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 mt-1">
            <Button variant="ghost" size="icon" className={`h-8 w-8 rounded-lg ${isLight ? "text-gray-500 hover:text-gray-900 hover:bg-gray-100" : "text-[#A1A1AA] hover:text-white hover:bg-white/[0.04]"}`} asChild data-testid="sidebar-settings-btn">
              <Link to="/profile"><Settings className="w-4 h-4" /></Link>
            </Button>
            <Button variant="ghost" size="icon" className={`h-8 w-8 rounded-lg ml-auto ${isLight ? "text-red-500 hover:text-red-600 hover:bg-red-50" : "text-[#FF3B30] hover:text-[#FF453A] hover:bg-[#FF3B30]/10"}`} onClick={() => window.location.href = logoutUrl} data-testid="sidebar-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
